import { z } from "zod";
import { UuidSchema, TimestampsSchema, DateTimeSchema } from "./common.js";
import { IconNameSchema } from "./icon.js";
import { EntrypointUrlSchema, ImportSourceSchema, ImportCampaignDataSchema, } from "./vast.js";
// Stored as an integer on the DB (mirrors SegmentType). Values are stable —
// appending a new variant is safe (use the next integer); renumbering breaks
// existing rows.
export var CampaignType;
(function (CampaignType) {
    CampaignType[CampaignType["Standard"] = 0] = "Standard";
    CampaignType[CampaignType["Transit"] = 1] = "Transit";
})(CampaignType || (CampaignType = {}));
export const CAMPAIGN_TYPE_LABELS = {
    [CampaignType.Standard]: "Standard",
    [CampaignType.Transit]: "Transit",
};
export const CampaignTypeSchema = z.nativeEnum(CampaignType);
// A transit (RTB) campaign's config; persisted inside the campaign's `metadata`.
export const TransitCampaignMetadataSchema = z.object({
    entrypoint: EntrypointUrlSchema,
    margin: z.number().int().min(0),
    qps: z.number().int().positive(),
    selectedPublishers: z.array(UuidSchema),
    blockList: z.array(z.string()),
});
// Fields are declared optional explicitly (not `.partial().extend()`, which
// does not compose under Zod 4) so a non-imported campaign may carry only
// `selectedPublishers` while imported campaigns keep full provenance. Empty/
// absent `selectedPublishers` ⇒ eligible for ALL providers. Kept field-
// compatible with CampaignImportMetadataSchema.
export const StandardCampaignMetadataSchema = z.object({
    source: ImportSourceSchema.optional(),
    url: z.string().optional(),
    importedAt: z.string().optional(),
    parsed: ImportCampaignDataSchema.optional(),
    raw: z.string().optional(),
    impressionDomain: z.string().nullable().optional(),
    selectedPublishers: z.array(UuidSchema).optional(),
});
// The `metadata` jsonb column is a closed set of known shapes: a transit
// campaign's config, or a standard campaign's import provenance + provider
// allow-list. `.strict()` on both branches keeps union discrimination
// order-insensitive — a malformed transit object cannot silently degrade to
// "standard", and a standard object cannot masquerade as transit. Kept 1-1
// with the DB column so persisted rows, API responses, and the frontend agree.
export const CampaignMetadataSchema = z.union([
    TransitCampaignMetadataSchema.strict(),
    StandardCampaignMetadataSchema.strict(),
]);
export const CampaignStatusSchema = z.enum([
    "draft",
    "pending",
    "approved",
    "active",
    "paused",
    "completed",
]);
export const LinkedCreativeSchema = z.object({
    uuid: UuidSchema,
    name: z.string().nullable(),
    url: z.string(),
    width: z.number().nullable(),
    height: z.number().nullable(),
    durationSeconds: z.number().nullable(),
});
export const CampaignBaseSchema = z
    .object({
    uuid: UuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    status: z.string(),
    type: CampaignTypeSchema,
    selectedSegments: z.array(UuidSchema),
    userId: UuidSchema.nullable(),
    frequency: z.number(),
    defaultCreativeId: UuidSchema.nullable(),
    startDate: DateTimeSchema.nullable(),
    endDate: DateTimeSchema.nullable(),
    icon: IconNameSchema.nullable(),
    cpm: z.number(),
    dailyFrequency: z.number().int().nullable(),
    weeklyFrequency: z.number().int().nullable(),
    impressionsCap: z.number().int().nullable(),
    budgetCap: z.number().nullable(),
    impressionDomain: z.string().nullable().optional(),
    creatives: z.array(LinkedCreativeSchema).optional(),
    metadata: CampaignMetadataSchema.nullable(),
})
    .merge(TimestampsSchema);
export const SegmentCreativesMapSchema = z.record(UuidSchema, UuidSchema);
export const CreateCampaignSchema = z
    .object({
    name: z.string().min(1),
    description: z.string().optional(),
    selectedSegments: z.array(UuidSchema),
    frequency: z.number().int().positive().optional().default(1),
    defaultCreativeId: UuidSchema.nullable().optional(),
    startDate: z.string().datetime().optional(),
    endDate: z.string().datetime().optional(),
    icon: IconNameSchema.nullable().optional(),
    cpm: z.number().min(0).optional().default(0),
    dailyFrequency: z.number().int().positive().nullable().optional(),
    weeklyFrequency: z.number().int().positive().nullable().optional(),
    impressionsCap: z.number().int().positive().nullable().optional(),
    budgetCap: z.number().min(0).nullable().optional(),
    segmentCreatives: SegmentCreativesMapSchema.optional(),
    type: CampaignTypeSchema.optional().default(CampaignType.Standard),
    metadata: CampaignMetadataSchema.optional(),
})
    // Publisher rtb+allowTransit is validated server-side (Zod can't see the DB).
    .superRefine((data, ctx) => {
    const isTransitMeta = data.metadata != null && "entrypoint" in data.metadata;
    if (data.type === CampaignType.Transit) {
        if (!isTransitMeta) {
            ctx.addIssue({
                code: "custom",
                message: "transit campaign requires transit metadata",
                path: ["metadata"],
            });
        }
        if (data.selectedSegments && data.selectedSegments.length > 0) {
            ctx.addIssue({
                code: "custom",
                message: "transit campaign cannot use segments",
                path: ["selectedSegments"],
            });
        }
    }
    else if (isTransitMeta) {
        ctx.addIssue({
            code: "custom",
            message: "standard campaign cannot have transit metadata",
            path: ["metadata"],
        });
    }
});
export const UpdateCampaignSchema = z
    .object({
    name: z.string().min(1).optional(),
    description: z.string().optional(),
    selectedSegments: z.array(UuidSchema).optional(),
    frequency: z.number().int().positive().optional(),
    defaultCreativeId: UuidSchema.nullable().optional(),
    startDate: z.string().datetime().optional(),
    endDate: z.string().datetime().optional(),
    icon: IconNameSchema.nullable().optional(),
    cpm: z.number().min(0).optional(),
    dailyFrequency: z.number().int().positive().nullable().optional(),
    weeklyFrequency: z.number().int().positive().nullable().optional(),
    impressionsCap: z.number().int().positive().nullable().optional(),
    budgetCap: z.number().min(0).nullable().optional(),
    segmentCreatives: SegmentCreativesMapSchema.optional(),
    type: CampaignTypeSchema.optional().default(CampaignType.Standard),
    metadata: CampaignMetadataSchema.optional(),
})
    .superRefine((data, ctx) => {
    const isTransitMeta = data.metadata != null && "entrypoint" in data.metadata;
    if (data.type === CampaignType.Transit) {
        if (!isTransitMeta) {
            ctx.addIssue({
                code: "custom",
                message: "transit campaign requires transit metadata",
                path: ["metadata"],
            });
        }
        if (data.selectedSegments && data.selectedSegments.length > 0) {
            ctx.addIssue({
                code: "custom",
                message: "transit campaign cannot use segments",
                path: ["selectedSegments"],
            });
        }
    }
    else if (isTransitMeta) {
        ctx.addIssue({
            code: "custom",
            message: "standard campaign cannot have transit metadata",
            path: ["metadata"],
        });
    }
});
export const SetCampaignCreativesSchema = z.object({
    segmentCreatives: SegmentCreativesMapSchema,
});
export const CampaignResponseSchema = CampaignBaseSchema;
export const CampaignWithReceiversSchema = CampaignBaseSchema.extend({
    totalReceivers: z.number(),
    uniqueReceivers: z.number(),
});
// Pagination envelope used by paged list endpoints. (PaginationQuerySchema in
// common.ts is the inbound query shape; this one is the outbound response.)
export const PaginationEnvelopeSchema = z.object({
    limit: z.number(),
    offset: z.number(),
    total: z.number().optional(),
    hasMore: z.boolean(),
});
export const CampaignListResponseSchema = z.object({
    data: z.array(CampaignWithReceiversSchema),
    pagination: PaginationEnvelopeSchema,
});
export const CampaignAnalyticsSchema = z.object({
    campaignId: UuidSchema,
    days: z.number(),
    totalServed: z.number(),
    uniqueDevices: z.number(),
    dailyBreakdown: z.array(z.object({
        date: z.string(),
        served: z.number(),
        uniqueDevices: z.number(),
    })),
    segmentBreakdown: z.array(z.object({
        segmentId: UuidSchema,
        served: z.number(),
        uniqueDevices: z.number(),
    })),
});
export const CampaignCostSchema = z.object({
    estimatedCost: z.number().nullable(),
    realCost: z.number().nullable(),
});
// Lazy-loaded per-campaign all-time receivers stats. Cached on the backend
// in a 10-minute LRU. `spent` is Σ adServed.buyPrice / 1000 (the income
// board's buy-price basis, not cpm × impressions) and `confirmedImpressions`
// counts adImpression beacons for the campaign's serves.
export const CampaignStatisticsSchema = z.object({
    totalReceivers: z.number(),
    uniqueReceivers: z.number(),
    spent: z.number(),
    confirmedImpressions: z.number(),
});
export const CampaignAnalyticsQuerySchema = z.object({
    days: z.string().optional().default("30"),
});
//# sourceMappingURL=campaign.js.map